import { useCallback } from "react";
import { buildFilterGroupFromColumnState } from "@/lib/filters";
import type { AIFieldDefinition } from "@/types/deep-research-reports";
import type { UnifiedTableColumn } from "@/types/table-column";
import type { AIFieldsQueryParams } from "./api/use-ai-fields";
import { type BaseListFilters, useListFilters } from "./use-list-filters";

// Default sort settings
const DEFAULT_SORT_COLUMN = "created_at";
const DEFAULT_SORT_DIRECTION = "desc" as const;

// SessionStorage keys
const AI_FIELDS_LIST_FILTERS_KEY = "ai-fields::list::filters";

/**
 * Hook for managing AI fields list filters
 * @returns Filters and utilities for managing AI fields list filters
 */
export function useAIFieldsListFilters() {
  const {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams: getBaseQueryParams,
  } = useListFilters<
    BaseListFilters,
    UnifiedTableColumn<AIFieldDefinition>,
    AIFieldsQueryParams
  >({
    storageKey: AI_FIELDS_LIST_FILTERS_KEY,
    defaultSortColumn: DEFAULT_SORT_COLUMN,
    defaultSortDirection: DEFAULT_SORT_DIRECTION,
    buildQueryParams,
  });

  // Helper function to build query params
  function buildQueryParams(
    filters: BaseListFilters,
    options?: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<AIFieldDefinition>[];
      limit?: number;
    },
  ): AIFieldsQueryParams {
    const { searchTerm, columns = [], limit = 50 } = options || {};

    // Sort handling
    let sortCol = filters.sortColumn || DEFAULT_SORT_COLUMN;

    // Fallback if sort column no longer exists in current column set
    const validSortKeys = columns.map((c) => c.key);
    if (!validSortKeys.includes(sortCol)) {
      sortCol = DEFAULT_SORT_COLUMN;
    }

    const sortDir = filters.sortDirection || DEFAULT_SORT_DIRECTION;

    const params: AIFieldsQueryParams = {
      sort_by: sortCol,
      sort_direction: sortDir,
      search: searchTerm || filters.search || undefined,
      limit,
    };

    // Build structured filters JSON from column state
    const validColumnKeys = new Set(columns.map((c) => c.key.toString()));
    const group = buildFilterGroupFromColumnState(filters, validColumnKeys);
    if (group) {
      params.filters = JSON.stringify(group);
    }

    return params;
  }

  // Wrapper for getQueryParams
  const getQueryParams = useCallback(
    (options?: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<AIFieldDefinition>[];
      limit?: number;
    }) => {
      return getBaseQueryParams(options);
    },
    [getBaseQueryParams],
  );

  return {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams,
  };
}
